import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Stores
import { useAuthStore } from './stores/authStore';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';

// Public Pages
import LandingPage from './pages/public/LandingPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import PendingApprovalPage from './pages/auth/PendingApprovalPage';
import AuthCallback from './pages/AuthCallback';

// Dashboard Pages
import DashboardPage from './pages/app/DashboardPage';
import SimulationsPage from './pages/app/SimulationsPage';
import LaunchSimulationPage from './pages/app/LaunchSimulationPage';
import SimulationDetailPage from './pages/app/SimulationDetailPage';
import CustomGeometryPage from './pages/app/CustomGeometryPage';
import { PAGE_GEOMETRIE_LIBRE_VISIBLE } from './config/pagesHistoriques';
import DocumentationPage from './pages/app/DocumentationPage';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import PendingApprovalsPage from './pages/admin/PendingApprovalsPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import ProjectsAdminPage from './pages/admin/ProjectsAdminPage';
import DiagnosticsAdminPage from './pages/admin/DiagnosticsAdminPage';
import PendingSimulationsAdminPage from './pages/admin/PendingSimulationsAdminPage';

// Project Pages
import ProjectsPage from './pages/projects/ProjectsPage';
import ProjectDetailPage from './pages/projects/ProjectDetailPage';

/**
 * Loading spinner for auth initialization
 */
const LoadingSpinner = () => (
  <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
  </div>
);

/**
 * Protected Route - Requires authentication
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isInitialized, isApproved } = useAuthStore();

  // Wait for auth initialization
  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is approved (uses store helper that works with both Cognito and legacy)
  if (!isApproved()) {
    return <Navigate to="/pending-approval" replace />;
  }

  return children;
};

/**
 * Admin Route - Requires admin role
 */
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isInitialized, isAdmin } = useAuthStore();

  // Wait for auth initialization
  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin()) {
    return <Navigate to="/app" replace />;
  }

  return children;
};

/**
 * Superadmin Route - Requires superadmin role
 */
const SuperadminRoute = ({ children }) => {
  const { isAuthenticated, isInitialized, isSuperadmin } = useAuthStore();

  // Wait for auth initialization
  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperadmin || !isSuperadmin()) {
    return <Navigate to="/app" replace />;
  }

  return children;
};

/**
 * Guest Route - Redirects authenticated users
 */
const GuestRoute = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuthStore();

  // Wait for auth initialization
  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  return children;
};

function App() {
  // Initialize auth on app load - only once
  useEffect(() => {
    useAuthStore.getState().initialize();
  }, []); // Empty dependency array - run only on mount

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />

        {/* Auth Routes - Guest only */}
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          path="/register"
          element={
            <GuestRoute>
              <RegisterPage />
            </GuestRoute>
          }
        />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />

        {/* OAuth Callback Route */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Protected Dashboard Routes */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard Home */}
          <Route index element={<DashboardPage />} />

          {/* Simulations */}
          <Route path="simulations" element={<SimulationsPage />} />
          <Route path="simulations/launch" element={<LaunchSimulationPage />} />
          {PAGE_GEOMETRIE_LIBRE_VISIBLE && (
            <Route path="simulations/custom-geometry" element={<CustomGeometryPage />} />
          )}
          <Route path="simulations/:id" element={<SimulationDetailPage />} />

          {/* Documentation */}
          <Route path="docs" element={<DocumentationPage />} />

          {/* Projects */}
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:projectId" element={<ProjectDetailPage />} />

          {/* Redirect old routes to new unified routes */}
          <Route path="drone-wing" element={<Navigate to="/app/simulations" replace />} />
          <Route path="drone-wing/launch" element={<Navigate to="/app/simulations/launch" replace />} />
          <Route path="drone-wing/:id" element={<Navigate to="/app/simulations/:id" replace />} />
          <Route path="mach-wing" element={<Navigate to="/app/simulations" replace />} />
          <Route path="mach-wing/launch" element={<Navigate to="/app/simulations/launch" replace />} />
          <Route path="mach-wing/:id" element={<Navigate to="/app/simulations/:id" replace />} />
        </Route>

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <DashboardLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="registrations" element={<PendingApprovalsPage />} />
          <Route path="users" element={<UserManagementPage />} />
        </Route>

        {/* Superadmin Routes */}
        <Route
          path="/superadmin"
          element={
            <SuperadminRoute>
              <DashboardLayout />
            </SuperadminRoute>
          }
        >
          <Route index element={<ProjectsAdminPage />} />
          <Route path="projects" element={<ProjectsAdminPage />} />
          <Route path="diagnostics" element={<DiagnosticsAdminPage />} />
          {/* Lien du mail de veille : calculs bloqués faute de machine Spot */}
          <Route path="en-attente" element={<PendingSimulationsAdminPage />} />
        </Route>

        {/* Legacy URL Support - Redirect old URLs to new routes */}
        <Route path="/dafoam-dashboard" element={<Navigate to="/app/simulations" replace />} />
        <Route path="/dafoam-launch" element={<Navigate to="/app/simulations/launch" replace />} />
        <Route path="/mach-wing-launch" element={<Navigate to="/app/simulations/launch" replace />} />
        <Route path="/mach-wing-dashboard" element={<Navigate to="/app/simulations" replace />} />

        {/* 404 - Redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
